import {
    Button,
    DisplayOrInput,
    FingerprintDisplay,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    RoleType,
    Field,
} from "modules/common";
import { ConfirmationModal } from "modules/common/components/ConfirmationModal";
import { useContext, useEffect, useMemo, useState } from "react";
import {
    useCreateMobileApp,
    useDeleteMobileApp,
    useEditMobileApp,
} from "modules/mobileAppsPortal/hooks/mobileAppsHooks";
import { MobileApp, MobileAppPaginatedResponse } from "services/mobileAppsPortal/response";
import { Dropzone } from "modules/common/components/Dropzone";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { isSuperAdmin } from "modules/auth/authorization/roleChecks";
import {
    appName,
    appDescription,
    androidLink,
    iosLink,
    androidVersion,
    iosVersion,
    ePoljoprivredaName,
    ePoljoprivredaId,
} from "modules/mobileAppsPortal/constants/inputConstants";
import { AccessibilityContext } from "views/AccessibilityContext";

export interface IMobileAppDetailsModal {
    selectedMobileApp?: MobileApp;
    isOpen: boolean;
    handleClose: () => void;
    mobileAppsData?: MobileAppPaginatedResponse;
    page?: number;
    onPageChange?: (page: number) => void;
}

const emptyMobileApp: MobileApp = {
    guid: "",
    name: "",
    description: "",
    imageUrl: "",
    linkAndroid: "",
    linkIOS: "",
    versionAndroid: "",
    versionIOS: "",
    ePoljoprivredaName: "",
    ePoljoprivredaId: null,
    createdBy: "",
    created: "",
    lastUpdatedBy: "",
    lastUpdated: "",
};

type MobileAppProperty = keyof MobileApp;

type MobileAppErrors = Partial<Record<MobileAppProperty, string>>;

export const MobileAppDetailsModal = ({
    selectedMobileApp,
    isOpen,
    handleClose,
    mobileAppsData,
    page,
    onPageChange,
}: IMobileAppDetailsModal) => {
    const [mobileApp, setMobileApp] = useState<MobileApp>(emptyMobileApp);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<MobileAppErrors>({});
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [showDelete, setShowDelete] = useState(false);
    const create = useCreateMobileApp(uploadedFile);
    const edit = useEditMobileApp(uploadedFile);
    const remove = useDeleteMobileApp();
    const { profile } = useSelector((state: RootState) => state.profile);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const mobileAppNames = mobileAppsData?.items.map((doc: MobileApp) => doc.name);

    const validateMobileApp = (mobileApp: MobileApp): MobileAppErrors => {
        const errors: MobileAppErrors = {};
        if (!mobileApp.name.trim()) {
            errors.name = "Naziv aplikacije je obavezan";
        }
        if (
            mobileAppNames?.includes(mobileApp.name) &&
            mobileApp.name !== selectedMobileApp?.name
        ) {
            errors.name = "Aplikacija s ovim imenom već postoji";
        }
        if (uploadedFile == null && isAdd) {
            errors.imageUrl = "Ikona aplikacije je obavezna";
        }
        if (!mobileApp.linkAndroid.trim() && !mobileApp.linkIOS.trim()) {
            errors.linkAndroid = "Obavezan je link za Android ili iOS aplikaciju";
            errors.linkIOS = "Obavezan je link za Android ili iOS aplikaciju";
        }
        if (mobileApp.linkAndroid.trim() && !mobileApp.versionAndroid.trim()) {
            errors.versionAndroid = "Obavezna je verzija Android aplikacije";
        }
        if (mobileApp.linkIOS.trim() && !mobileApp.versionIOS.trim()) {
            errors.versionIOS = "Obavezna je verzija iOS aplikacije";
        }
        return errors;
    };

    useEffect(() => {
        setMobileApp(selectedMobileApp ?? emptyMobileApp);
    }, [selectedMobileApp]);

    useEffect(() => {
        setErrors({});
        setIsEdit(false);
        if (!isOpen) {
            setMobileApp(emptyMobileApp);
            setUploadedFile(null);
        }
    }, [isOpen, setErrors, setIsEdit, selectedMobileApp]);

    const isAdd = useMemo(() => !selectedMobileApp, [selectedMobileApp]);

    const title = useMemo(() => {
        if (isAdd) {
            return "Nova aplikacija";
        }
        if (isEdit) {
            return "Uredi aplikaciju";
        }
        return mobileApp?.name ?? "Detalji dokumenta";
    }, [isAdd, isEdit, mobileApp?.name]);

    const changeMobileAppProperty = (
        property: MobileAppProperty,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setErrors(prevState => ({ ...prevState, [property]: undefined }));
        setMobileApp(prevState => ({
            ...(prevState ?? emptyMobileApp),
            [property]: event.target.value,
        }));
    };

    const getErrorMsg = (property: MobileAppProperty) => {
        return errors[property] ?? "";
    };

    const onCancel = () => {
        setIsEdit(false);
        setErrors({});
    };

    const onSave = async () => {
        const newErrors = validateMobileApp(mobileApp);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setIsButtonDisabled(true);
        if (isAdd) {
            await create.mutateAsync(mobileApp);
        } else {
            await edit.mutateAsync(mobileApp);
        }

        setErrors({});
        handleClose();
        setIsButtonDisabled(false);
    };

    const onDelete = async () => {
        if (!mobileApp) {
            return;
        }
        const remainingItems = (mobileAppsData?.items?.length ?? 0) - 1;
        if (remainingItems <= 0 && (page ?? 1) > 1) {
            onPageChange && onPageChange((page ?? 1) - 1);
        }
        await remove.mutateAsync(mobileApp.guid);
        setShowDelete(false);
        handleClose();
    };
    const { lowVision } = useContext(AccessibilityContext);
    return (
        <Modal
            isActive={isOpen}
            className="mobile-app-details-modal"
            onClose={handleClose}
            modalHeader={<ModalHeader onClose={handleClose} title={title} />}
            modalFooter={
                isSuperAdmin(profile?.roleType?.value ?? 0) &&
                (!mobileApp ? null : (
                    <ModalFooter>
                        {isAdd || isEdit ? (
                            <>
                                {isEdit && (
                                    <Button
                                        type="button"
                                        size="small"
                                        variant="primary-ghost"
                                        disabled={false}
                                        onClick={onCancel}
                                    >
                                        <span>ODUSTANI</span>
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary"
                                    disabled={isButtonDisabled}
                                    onClick={onSave}
                                >
                                    <span>SPREMI</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="warning-ghost"
                                    disabled={profile?.roleType?.value !== RoleType.SuperAdmin}
                                    onClick={() => setShowDelete(true)}
                                >
                                    <span>OBRIŠI</span>
                                </Button>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary-ghost"
                                    disabled={profile?.roleType?.value !== RoleType.SuperAdmin}
                                    onClick={() => setIsEdit(true)}
                                >
                                    <span>UREDI</span>
                                </Button>
                            </>
                        )}
                    </ModalFooter>
                ))
            }
        >
            <div className="mobile-app-details">
                <div className="modal-section">
                    <Field
                        name={isAdd ? "DODAJ DATOTEKU" : isEdit ? "ZAMIJENI DATOTEKU" : "DATOTEKA"}
                        errorMessage={getErrorMsg("imageUrl")}
                    >
                        {isAdd || isEdit ? (
                            <Dropzone
                                handleDrop={(files: File[]) => {
                                    if (files.length > 0) setUploadedFile(files[0]);
                                    setErrors(prevState => ({
                                        ...prevState,
                                        imageUrl: undefined,
                                    }));
                                }}
                                handleDelete={() => {
                                    setUploadedFile(null);
                                    setErrors(prevState => ({
                                        ...prevState,
                                        imageUrl: undefined,
                                    }));
                                }}
                                files={uploadedFile ? [uploadedFile] : []}
                            />
                        ) : (
                            <span
                                className={lowVision ? "low-vision-dropzone" : "dropzone-file-name"}
                            >
                                {mobileApp?.name}
                            </span>
                        )}
                    </Field>
                </div>
                <div className="modal-section">
                    <Field name="NAZIV APLIKACIJE" errorMessage={getErrorMsg(appName)}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.name}
                            <Input
                                id={appName}
                                type="text"
                                size="small"
                                name={appName}
                                value={mobileApp?.name}
                                onChange={changeMobileAppProperty.bind(null, appName)}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
                <div className="modal-section">
                    <Field name="OPIS" errorMessage={getErrorMsg(appDescription)}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.description}
                            <Input
                                id={appDescription}
                                type="text"
                                size="small"
                                name={appDescription}
                                value={mobileApp?.description}
                                onChange={changeMobileAppProperty.bind(null, appDescription)}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
                <div className="modal-section">
                    <Field name="ANDROID APLIKACIJA" errorMessage={getErrorMsg(androidLink)}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.linkAndroid}
                            <Input
                                id={androidLink}
                                type="text"
                                size="small"
                                name={androidLink}
                                value={mobileApp?.linkAndroid}
                                onChange={changeMobileAppProperty.bind(null, androidLink)}
                            />
                        </DisplayOrInput>
                    </Field>
                    <Field name="VERZIJA" errorMessage={getErrorMsg(androidVersion)}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.versionAndroid}
                            <Input
                                id={androidVersion}
                                type="text"
                                size="small"
                                name={androidVersion}
                                value={mobileApp?.versionAndroid}
                                onChange={changeMobileAppProperty.bind(null, androidVersion)}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
                <div className="modal-section">
                    <Field name="iOS APLIKACIJA" errorMessage={getErrorMsg(iosLink)}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.linkIOS}
                            <Input
                                id={iosLink}
                                type="text"
                                size="small"
                                name={iosLink}
                                value={mobileApp?.linkIOS}
                                onChange={changeMobileAppProperty.bind(null, iosLink)}
                            />
                        </DisplayOrInput>
                    </Field>
                    <Field name="VERZIJA" errorMessage={getErrorMsg(iosVersion)}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.versionIOS}
                            <Input
                                id={iosVersion}
                                type="text"
                                size="small"
                                name={iosVersion}
                                value={mobileApp?.versionIOS}
                                onChange={changeMobileAppProperty.bind(null, iosVersion)}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
                <div className="modal-section">
                    <Field
                        name="NAZIV PODSUSTAVA ePOLJOPRIVREDE"
                        errorMessage={getErrorMsg(ePoljoprivredaName)}
                    >
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.ePoljoprivredaName}
                            <Input
                                id={ePoljoprivredaName}
                                type="text"
                                size="small"
                                name={ePoljoprivredaName}
                                value={mobileApp?.ePoljoprivredaName}
                                onChange={changeMobileAppProperty.bind(null, ePoljoprivredaName)}
                            />
                        </DisplayOrInput>
                    </Field>
                    <Field name="ePOLJOPRIVREDA ID" errorMessage={getErrorMsg(ePoljoprivredaId)}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {mobileApp?.ePoljoprivredaId}
                            <Input
                                id={ePoljoprivredaId}
                                type="number"
                                size="small"
                                name={ePoljoprivredaId}
                                value={mobileApp?.ePoljoprivredaId}
                                onChange={changeMobileAppProperty.bind(null, ePoljoprivredaId)}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
                {(mobileApp.created !== "" || mobileApp.lastUpdated !== "") &&
                    profile?.roleType?.value === RoleType.SuperAdmin && (
                        <>
                            <div className="modal-section narrow">
                                <FingerprintDisplay
                                    item={mobileApp}
                                    value="created"
                                    containerClassName="modal-section no-border"
                                    labelClassName="modal-details-label"
                                    textClassName="modal-details-text"
                                />
                            </div>
                            <div className="modal-section narrow">
                                <FingerprintDisplay
                                    item={mobileApp}
                                    value="lastUpdated"
                                    containerClassName="modal-section no-border"
                                    labelClassName="modal-details-label"
                                    textClassName="modal-details-text"
                                />
                            </div>
                        </>
                    )}
            </div>
            <ConfirmationModal
                isActive={showDelete}
                onConfirm={onDelete}
                onCancel={() => setShowDelete(false)}
                content={`Želite li izbrisati aplikaciju ${mobileApp?.name}?`}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
            />
        </Modal>
    );
};
